import { useState } from 'react'
import { HelpCircle } from 'lucide-react'
import { getOrCreateSessionId } from '@/utils/sessionId'
import { submitNoFeedback } from '@/services/validationApi'
import type { Slide } from './slideData'

interface NoFeedbackFormProps {
  slide: Slide | null
  onDone: () => void
}

export default function NoFeedbackForm({ slide, onDone }: NoFeedbackFormProps) {
  const [solution, setSolution] = useState('')
  const [loading, setLoading] = useState(false)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!slide) return
    setLoading(true)
    try {
      const sessionId = getOrCreateSessionId()
      await submitNoFeedback({
        sessionId,
        slideIndex: slide.index,
        slideTitle: slide.title,
        currentSolution: solution,
      })
    } catch {
      // Silent fail — feedback is not critical path
    } finally {
      setLoading(false)
      onDone()
    }
  }

  return (
    <div className="w-full max-w-md mx-auto px-4">
      <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8">
        {/* Header */}
        <div className="mb-6 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-50">
            <HelpCircle className="h-7 w-7 text-amber-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900">That's fair.</h2>
          <p className="mt-2 text-sm leading-relaxed text-gray-500">
            Help us understand — how does your team handle{' '}
            <strong className="text-gray-700">"{slide?.title}"</strong> today?
          </p>
          <p className="mt-1 text-xs text-gray-400">
            Your answer directly shapes what we build. This takes 30 seconds.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              How do you solve this currently?
            </label>
            <textarea
              value={solution}
              onChange={(e) => setSolution(e.target.value)}
              rows={4}
              placeholder="We use a shared Excel sheet / We hired a dedicated coordinator / Our clients use a WhatsApp group..."
              className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
          </div>

          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 rounded-lg bg-blue-600 py-3 text-sm font-semibold text-white transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
            >
              {loading ? 'Sending...' : 'Submit & Continue \u2192'}
            </button>
            <button
              type="button"
              className="rounded-lg px-4 py-3 text-sm text-gray-400 hover:text-gray-600 transition-colors"
              onClick={onDone}
            >
              Skip
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
